import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getBlogPosts, formatBlogDate, BLOG_UI } from "@/content/blogPosts";
import { useLang } from "@/i18n/LangContext";
import { ArrowLeft, Clock, CalendarDays, ArrowRight } from "lucide-react";

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export function useSeoTags({ title, description }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: document.querySelector('meta[name="description"]')?.getAttribute("content"),
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.querySelector('meta[property="og:description"]')?.getAttribute("content"),
    };
    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    return () => {
      document.title = prev.title;
      if (prev.desc) setMeta('meta[name="description"]', "content", prev.desc);
      if (prev.ogTitle) setMeta('meta[property="og:title"]', "content", prev.ogTitle);
      if (prev.ogDesc) setMeta('meta[property="og:description"]', "content", prev.ogDesc);
    };
  }, [title, description]);
}

const Block = ({ paras = [], bullets = [], paras2 = [] }) => (
  <>
    {paras.map((p, i) => (
      <p key={`p${i}`} className="mt-4 text-base leading-relaxed text-slate-600">{p}</p>
    ))}
    {bullets.length > 0 && (
      <ul className="mt-4 space-y-2.5 pl-1">
        {bullets.map((b, i) => (
          <li key={`b${i}`} className="flex gap-3 text-base leading-relaxed text-slate-600">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    {paras2.map((p, i) => (
      <p key={`p2${i}`} className="mt-4 text-base leading-relaxed text-slate-600">{p}</p>
    ))}
  </>
);

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang } = useLang();
  const ui = BLOG_UI[lang] || BLOG_UI.en;
  const posts = getBlogPosts(lang);
  const a = posts.find((x) => x.slug === slug);
  useSeoTags({
    title: a ? `${a.title} | Dotec EW-PDF` : "Blog | Dotec EW-PDF",
    description: a ? a.meta : "",
  });
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!a) return <Navigate to="/blog" replace />;
  const idx = posts.indexOf(a);
  const next = posts[(idx + 1) % posts.length];
  return (
    <div className="mx-auto max-w-3xl px-6 py-12 lg:px-8" data-testid="blog-article">
      <div className="mb-6 flex justify-start">
        <Link to="/blog" data-testid="blog-back-link" className="group inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition-all hover:bg-emerald-500 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {ui.backAll}
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs font-bold uppercase tracking-widest text-emerald-600">
        <span className="flex items-center gap-1.5" data-testid="blog-article-date"><CalendarDays className="h-3.5 w-3.5" /> {formatBlogDate(a.date, lang)}</span>
        <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {a.minutes} {ui.min}</span>
      </div>
      <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="blog-article-title">{a.title}</h1>
      <p className="mt-4 text-lg leading-relaxed text-slate-500">{a.intro}</p>
      {a.sections.map((s, i) => (
        <section key={i} className="mt-10">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{s.h2}</h2>
          <Block paras={s.paras} bullets={s.bullets} paras2={s.paras2} />
        </section>
      ))}
      <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-8 text-center" data-testid="blog-article-cta">
        <h2 className="font-display text-2xl font-extrabold text-slate-900">{ui.cta}</h2>
        <p className="mx-auto mt-2 max-w-lg text-slate-600">{ui.ctaSub}</p>
        <Link to={a.tool} data-testid="blog-cta-tool-link" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-7 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700">
          {a.toolLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-10 border-t border-slate-200 pt-6">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{ui.next}</p>
        <Link to={`/blog/${next.slug}`} data-testid="blog-next-article" className="group mt-2 block">
          <span className="font-display text-lg font-bold text-slate-900 group-hover:text-emerald-700">{next.title}</span>
        </Link>
      </div>
    </div>
  );
}
